import { useState } from 'react';
import { startSponsor } from '@/lib/coreApi';

const CORE_API = import.meta.env.VITE_CORE_API_URL || '';
const STATUS_IDLE = 'idle';
const STATUS_LOADING = 'loading';

interface FormMessage {
  type: 'success' | 'error' | 'info';
  text: string;
}

function makeMessage(type: 'success' | 'error' | 'info', text: string): FormMessage {
  return { type, text };
}

function parseError(err: unknown): string {
  if (err instanceof Error && err.message) return err.message;
  if (typeof err === 'string') return err;
  return 'Something went wrong. Please try again.';
}

async function hashIdentifier(source: string): Promise<string> {
  const input = source.trim().toLowerCase();
  if (!input) return '';

  if (typeof window !== 'undefined' && window.crypto?.subtle) {
    const encoder = new TextEncoder();
    const data = encoder.encode(input);
    const digest = await window.crypto.subtle.digest('SHA-256', data);
    return Array.from(new Uint8Array(digest))
      .map(b => b.toString(16).padStart(2, '0'))
      .join('');
  }

  if (typeof window !== 'undefined' && typeof window.btoa === 'function') {
    return window.btoa(input);
  }

  return input;
}

export function SponsorStartForm() {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [status, setStatus] = useState(STATUS_IDLE);
  const [message, setMessage] = useState<FormMessage | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!email || status === STATUS_LOADING) return;
    setStatus(STATUS_LOADING);
    setMessage(null);

    try {
      await startSponsor({ email, display_name: name });

      setStatus('success');
      setMessage(makeMessage('success', 'Check your email for the $800 link.'));
      setEmail('');
      setName('');
    } catch (err) {
      setStatus('error');
      setMessage(makeMessage('error', parseError(err)));
    }
  }

  return (
    <form className="space-y-3" onSubmit={submit}>
      <div className="space-y-1">
        <label className="text-sm font-medium text-cocoa" htmlFor="sponsor-name">
          Name (optional)
        </label>
        <input
          id="sponsor-name"
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="w-full rounded-xl border border-sand bg-white/80 px-3 py-2 text-cocoa shadow-inner focus:outline-none focus:ring-2 focus:ring-mint"
          placeholder="Display name"
        />
      </div>
      <div className="space-y-1">
        <label className="text-sm font-medium text-cocoa" htmlFor="sponsor-email">
          Email
        </label>
        <input
          id="sponsor-email"
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="w-full rounded-xl border border-sand bg-white/80 px-3 py-2 text-cocoa shadow-inner focus:outline-none focus:ring-2 focus:ring-mint"
          placeholder="you@example.com"
        />
      </div>
      <button
        type="submit"
        disabled={!email || status === STATUS_LOADING}
        className="w-full rounded-xl bg-brick px-4 py-2 font-semibold text-white shadow-soft transition hover:bg-brickDark disabled:opacity-60"
      >
        {status === 'loading' ? 'Submitting…' : 'Become a sponsor'}
      </button>
      {message ? (
        <p
          className={`text-sm ${
            message.type === 'success'
              ? 'text-mint'
              : message.type === 'error'
              ? 'text-brick'
              : 'text-cocoa/80'
          }`}
        >
          {message.text}
        </p>
      ) : null}
    </form>
  );
}

export function ClassroomNominationForm() {
  const [classroomId, setClassroomId] = useState('');
  const [contact, setContact] = useState('');
  const [reason, setReason] = useState('');
  const [status, setStatus] = useState(STATUS_IDLE);
  const [message, setMessage] = useState<FormMessage | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!classroomId || !contact || status === STATUS_LOADING) return;
    setStatus(STATUS_LOADING);
    setMessage(null);

    try {
      const hash = await hashIdentifier(contact);
      if (!hash) {
        throw new Error('Unable to hash identifier.');
      }

      const res = await fetch(`${CORE_API}/api/v1/nominations/classroom`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ classroom_id: classroomId.trim(), nominator_hash: hash, reason }),
      });

      if (!res.ok) {
        throw new Error((await res.text()) || 'Nomination failed.');
      }

      setStatus('success');
      setMessage(makeMessage('success', 'Nomination submitted!'));
      setReason('');
    } catch (err) {
      setStatus('error');
      setMessage(makeMessage('error', parseError(err)));
    }
  }

  return (
    <form className="space-y-3" onSubmit={submit}>
      <div className="space-y-1">
        <label className="text-sm font-medium text-cocoa" htmlFor="nominate-classroom">
          Classroom ID
        </label>
        <input
          id="nominate-classroom"
          type="text"
          required
          value={classroomId}
          onChange={(e) => setClassroomId(e.target.value)}
          className="w-full rounded-xl border border-sand bg-white/80 px-3 py-2 text-cocoa shadow-inner focus:outline-none focus:ring-2 focus:ring-mint"
          placeholder="UUID from the teacher"
        />
      </div>
      <div className="space-y-1">
        <label className="text-sm font-medium text-cocoa" htmlFor="nominate-contact">
          Your email or phone (hashed privately)
        </label>
        <input
          id="nominate-contact"
          type="text"
          required
          value={contact}
          onChange={(e) => setContact(e.target.value)}
          className="w-full rounded-xl border border-sand bg-white/80 px-3 py-2 text-cocoa shadow-inner focus:outline-none focus:ring-2 focus:ring-mint"
          placeholder="Used to dedupe nominations"
        />
      </div>
      <div className="space-y-1">
        <label className="text-sm font-medium text-cocoa" htmlFor="nominate-reason">
          Why this classroom?
        </label>
        <textarea
          id="nominate-reason"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          className="min-h-[80px] w-full rounded-xl border border-sand bg-white/80 px-3 py-2 text-cocoa shadow-inner focus:outline-none focus:ring-2 focus:ring-mint"
          placeholder="Optional context"
        />
      </div>
      <button
        type="submit"
        disabled={!classroomId || !contact || status === STATUS_LOADING}
        className="w-full rounded-xl bg-cocoa px-4 py-2 font-semibold text-white shadow-soft transition hover:bg-cocoa/90 disabled:opacity-60"
      >
        {status === 'loading' ? 'Submitting…' : 'Nominate classroom'}
      </button>
      {message ? (
        <p
          className={`text-sm ${
            message.type === 'success'
              ? 'text-mint'
              : message.type === 'error'
              ? 'text-brick'
              : 'text-cocoa/80'
          }`}
        >
          {message.text}
        </p>
      ) : null}
    </form>
  );
}

export function TeacherConfirmationForm() {
  const [classroomId, setClassroomId] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState(STATUS_IDLE);
  const [message, setMessage] = useState<FormMessage | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!classroomId || !displayName || !email || status === STATUS_LOADING) return;
    setStatus(STATUS_LOADING);
    setMessage(null);

    try {
      const res = await fetch(`${CORE_API}/api/v1/nominations/teacher`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          nominator_name: displayName.trim(),
          nominator_email: email.trim(),
          teacher_name: displayName.trim(),
          teacher_email: email.trim(),
          school: classroomId.trim(),
        }),
      });

      if (!res.ok) {
        throw new Error((await res.text()) || 'Unable to request confirmation.');
      }

      setStatus('success');
      setMessage(makeMessage('success', 'Check your .edu email to verify.'));
    } catch (err) {
      setStatus('error');
      setMessage(makeMessage('error', parseError(err)));
    }
  }

  return (
    <form className="space-y-3" onSubmit={submit}>
      <div className="space-y-1">
        <label className="text-sm font-medium text-cocoa" htmlFor="confirm-classroom">
          Classroom ID
        </label>
        <input
          id="confirm-classroom"
          type="text"
          required
          value={classroomId}
          onChange={(e) => setClassroomId(e.target.value)}
          className="w-full rounded-xl border border-sand bg-white/80 px-3 py-2 text-cocoa shadow-inner focus:outline-none focus:ring-2 focus:ring-sky-300"
          placeholder="UUID provided by Project Tower"
        />
      </div>
      <div className="space-y-1">
        <label className="text-sm font-medium text-cocoa" htmlFor="confirm-name">
          Teacher display name
        </label>
        <input
          id="confirm-name"
          type="text"
          required
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          className="w-full rounded-xl border border-sand bg-white/80 px-3 py-2 text-cocoa shadow-inner focus:outline-none focus:ring-2 focus:ring-sky-300"
          placeholder="How we should recognize you"
        />
      </div>
      <div className="space-y-1">
        <label className="text-sm font-medium text-cocoa" htmlFor="confirm-email">
          School email (.edu required)
        </label>
        <input
          id="confirm-email"
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="w-full rounded-xl border border-sand bg-white/80 px-3 py-2 text-cocoa shadow-inner focus:outline-none focus:ring-2 focus:ring-sky-300"
          placeholder="teacher@school.edu"
        />
      </div>
      <button
        type="submit"
        disabled={!classroomId || !displayName || !email || status === STATUS_LOADING}
        className="w-full rounded-xl bg-sky-500 px-4 py-2 font-semibold text-white shadow-soft transition hover:bg-sky-600 disabled:opacity-60"
      >
        {status === 'loading' ? 'Sending…' : 'Confirm nomination'}
      </button>
      {message ? (
        <p
          className={`text-sm ${
            message.type === 'success'
              ? 'text-mint'
              : message.type === 'error'
              ? 'text-brick'
              : 'text-cocoa/80'
          }`}
        >
          {message.text}
        </p>
      ) : null}
    </form>
  );
}

export function AdminDrawLauncher() {
  const [slots, setSlots] = useState('1');
  const [status, setStatus] = useState(STATUS_IDLE);
  const [message, setMessage] = useState<FormMessage | null>(null);

  async function runDraw(e: React.FormEvent) {
    e.preventDefault();
    if (status === STATUS_LOADING) return;
    setStatus(STATUS_LOADING);
    setMessage(null);

    try {
      const res = await fetch('/api/v1/admin/draw/assign', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ slots: Number.parseInt(slots, 10) || 1 }),
      });

      if (!res.ok) {
        throw new Error((await res.text()) || 'Draw failed.');
      }

      const payload = await res.json();
      const assigned = payload?.assigned || [];
      setStatus('success');
      if (assigned.length) {
        setMessage(
          makeMessage(
            'success',
            `Assigned ${assigned.length} classroom${assigned.length === 1 ? '' : 's'}.`
          )
        );
      } else {
        setMessage(makeMessage('info', 'No classrooms assigned. Pool may be empty.'));
      }
    } catch (err) {
      setStatus('error');
      setMessage(makeMessage('error', parseError(err)));
    }
  }

  return (
    <form className="space-y-3" onSubmit={runDraw}>
      <div className="space-y-1">
        <label className="text-sm font-medium text-cocoa" htmlFor="draw-slots">
          Slots to assign
        </label>
        <input
          id="draw-slots"
          type="number"
          min="1"
          max="50"
          value={slots}
          onChange={(e) => setSlots(e.target.value)}
          className="w-32 rounded-xl border border-sand bg-white/80 px-3 py-2 text-cocoa shadow-inner focus:outline-none focus:ring-2 focus:ring-cocoa"
        />
      </div>
      <button
        type="submit"
        disabled={status === STATUS_LOADING}
        className="rounded-xl bg-cocoa px-4 py-2 font-semibold text-white shadow-soft transition hover:bg-cocoa/90 disabled:opacity-60"
      >
        {status === 'loading' ? 'Running…' : 'Run draw'}
      </button>
      {message ? (
        <p
          className={`text-sm ${
            message.type === 'success'
              ? 'text-mint'
              : message.type === 'error'
              ? 'text-brick'
              : 'text-cocoa/80'
          }`}
        >
          {message.text}
        </p>
      ) : null}
    </form>
  );
}
